import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import axios from 'axios'
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from 'reactflow'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import CropFreeIcon from '@mui/icons-material/CropFree'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import MapIcon from '@mui/icons-material/Map'
import 'reactflow/dist/style.css'
import { Box, Snackbar, Alert, Typography } from '@mui/material'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { getTheme } from './theme/theme'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { useCurrency } from './contexts/CurrencyContext'
import { supabase } from './supabaseClient'

// Components
import ActionToolbar from './components/ActionToolbar'
import ConfigPanel from './components/ConfigPanel'
import SimulationDashboard from './components/SimulationDashboard'
import ErrorBoundary from './components/ErrorBoundary'
import LoadingOverlay from './components/LoadingOverlay'
import ConfirmDialog from './components/ConfirmDialog'
import { validateNetwork } from './components/validationService'
import ValidationPanel from './components/ValidationPanel'
import ProtectedRoute from './components/ProtectedRoute'
import SavedNetworksDialog from './components/SavedNetworksDialog'
import WorkspaceStats from './components/WorkspaceStats'
import { WorkspaceVisibilityContext } from './contexts/WorkspaceVisibilityContext'
import SupplyChainNode from './components/SupplyChainNode'
import ScenarioComparison from './components/ScenarioComparison'
import PrivateRoute from './components/PrivateRoute'
import Preloader from './components/Preloader'
import MapView from './components/map/MapView'
import WorkspaceDemandLayer from './components/WorkspaceDemandLayer'
import ViewToggle from './components/ViewToggle'

// Pages
import Login from './pages/Login'
import Signup from './pages/Signup'
import LandingPage from './pages/LandingPage'
import ResetPassword from './pages/ResetPassword'
import ProfilePage from './pages/ProfilePage'
import CommunityHome from './pages/community/CommunityHome'
import NewPost from './pages/community/NewPost'
import PostDetail from './pages/community/PostDetail'
import Announcements from './pages/community/Announcements'
import Guidelines from './pages/community/Guidelines'

function AuthCallback() {
  const navigate = useNavigate()
  const { setUser } = useAuth()

  useEffect(() => {
    // Supabase automatically handles the OAuth hash fragments
    // We just need to wait for the session to be established
    const handleAuth = async () => {
      // Wait for Supabase to process the hash
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Check if we have a session now
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (session) {
        // Get redirect path
        const redirectPath = localStorage.getItem('redirectAfterLogin') || '/app'
        localStorage.removeItem('redirectAfterLogin')

        // Navigate
        window.location.href = redirectPath
      } else {
        // No session, go home
        window.location.href = '/'
      }
    }

    handleAuth()
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Typography sx={{ color: '#fff' }}>Completing sign in...</Typography>
    </Box>
  )
}

// ── Unified workspace controls (zoom + lock + minimap toggle) ────────────────
// Must be module-level so it can use useReactFlow (inside ReactFlow context)
function WorkspaceControls({ themeMode, showMinimap, onToggleMinimap, isLocked, onToggleLock }) {
  const { zoomIn, zoomOut, fitView } = useReactFlow()
  const isDark = themeMode === 'dark'

  const bg          = isDark ? '#1a1d29' : '#ffffff'
  const bgHover     = isDark ? '#252d3f' : '#f4f6f9'
  const iconColor   = isDark ? '#9ca3af' : '#4b5563'
  const iconHover   = isDark ? '#e2e8f0' : '#111827'
  const divider     = isDark ? '#2d3748' : '#e5e7eb'
  const shadow      = isDark
    ? '0 2px 12px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)'
    : '0 2px 12px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)'

  const btn = (active = false) => ({
    width: 28, height: 28,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    color: active ? '#6366f1' : iconColor,
    bgcolor: active ? (isDark ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.1)') : bg,
    transition: 'color 0.12s, background-color 0.12s',
    '&:hover': {
      bgcolor: active ? (isDark ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.16)') : bgHover,
      color: active ? '#818cf8' : iconHover,
    },
  })

  return (
    <Panel position="bottom-left">
      <Box sx={{
        display: 'flex', flexDirection: 'column',
        bgcolor: bg,
        border: `1px solid ${divider}`,
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: shadow,
      }}>
        <Box sx={btn()} onClick={() => zoomIn({ duration: 200 })}>
          <AddIcon sx={{ fontSize: 14 }} />
        </Box>
        <Box sx={{ height: '1px', bgcolor: divider }} />
        <Box sx={btn()} onClick={() => zoomOut({ duration: 200 })}>
          <RemoveIcon sx={{ fontSize: 14 }} />
        </Box>
        <Box sx={{ height: '1px', bgcolor: divider }} />
        <Box sx={btn()} onClick={() => fitView({ duration: 400, padding: 0.12 })}>
          <CropFreeIcon sx={{ fontSize: 14 }} />
        </Box>
        <Box sx={{ height: '1px', bgcolor: divider }} />
        <Box sx={btn(isLocked)} onClick={onToggleLock}>
          {isLocked
            ? <LockIcon sx={{ fontSize: 13 }} />
            : <LockOpenIcon sx={{ fontSize: 13 }} />
          }
        </Box>
        {/* Separator before minimap toggle */}
        <Box sx={{ height: '4px', bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }} />
        <Box sx={{ height: '1px', bgcolor: divider }} />
        <Box sx={btn(showMinimap)} onClick={onToggleMinimap}>
          <MapIcon sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    </Panel>
  )
}


function MainApp() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { currency } = useCurrency()

  // Theme - Keep your existing system
  const [themeMode, setThemeMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode')
    return savedMode || 'light'
  })

  const theme = useMemo(() => getTheme(themeMode), [themeMode])

  const nodeTypes = useMemo(() => ({ supplyChainNode: SupplyChainNode }), [])
  const edgeTypes = useMemo(() => ({}), [])

  const toggleTheme = () => {
    setThemeMode(prevMode => {
      const newMode = prevMode === 'light' ? 'dark' : 'light'
      localStorage.setItem('themeMode', newMode)
      return newMode
    })
  }

  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  // Stores user-dragged positions for virtual demand nodes {demandId: {x, y}}
  // Persisted to localStorage so positions survive page reloads
  const [demandNodePositions, setDemandNodePositions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('snw_demandNodePos') || '{}') } catch { return {} }
  })
  useEffect(() => {
    localStorage.setItem('snw_demandNodePos', JSON.stringify(demandNodePositions))
  }, [demandNodePositions])

  const handleDemandPositionChange = useCallback((demandId, pos) => {
    setDemandNodePositions(prev => ({ ...prev, [demandId]: pos }))
  }, [])

  const handleNodesChange = onNodesChange
  const [selectedNode, setSelectedNode] = useState(null)
  const [selectedEdge, setSelectedEdge] = useState(null)
  const [selectedDemandId, setSelectedDemandId] = useState(null)

  // Demands
  const [demands, setDemands] = useState([])

  // Simulation
  const [simulationResults, setSimulationResults] = useState(null)
  const [isSimulating, setIsSimulating] = useState(false)
  const [showSimAnimation, setShowSimAnimation] = useState(false)
  const [simTime, setSimTime] = useState(30)
  const [dashboardNodeId, setDashboardNodeId] = useState('network')
  const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
      ? window.location.origin + '/api'
      : 'http://localhost:8000')

  // Scenarios
  const [scenarios, setScenarios] = useState([])
  const [showComparison, setShowComparison] = useState(false)

  // Networks
  const [savedNetworks, setSavedNetworks] = useState([])
  const [showSavedNetworks, setShowSavedNetworks] = useState(false)
  const [currentNetworkId, setCurrentNetworkId] = useState(null)

  // Map state
  const [viewMode, setViewMode] = useState('workspace')
  const [mapPlacementMode, setMapPlacementMode] = useState(null)
  const [isMapFullscreen, setIsMapFullscreen] = useState(false)
  const [showMinimap, setShowMinimap] = useState(true)
  const [isWorkspaceLocked, setIsWorkspaceLocked] = useState(false)
  const canvasContainerRef = useRef(null)

  const handleToggleFullscreen = useCallback(async () => {
    if (!canvasContainerRef.current) return
    if (!document.fullscreenElement) {
      await canvasContainerRef.current.requestFullscreen()
    } else {
      await document.exitFullscreen()
    }
  }, [])

  useEffect(() => {
    const onFsChange = () => setIsMapFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  // UI State
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const [isLoading, setIsLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    severity: 'warning',
  })

  // Visibility toggles — shared across workspace + map views
  const [visibility, setVisibility] = useState({
    supplier: true, factory: true, distributor: true, retailer: true,
    demand: true, labels: true, connections: true,
  })
  const handleToggleVisibility = useCallback(key => {
    setVisibility(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // Panel resize
  const [configPanelWidth, setConfigPanelWidth] = useState(340)
  const isResizingConfig = useRef(false)

  // Validation
  const [showValidation, setShowValidation] = useState(false)
  const [validationResults, setValidationResults] = useState(null)

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState('saved')
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null)
  const [currentNetworkName, setCurrentNetworkName] = useState('Untitled Network')

  // Undo/Redo history
  const historyRef = useRef({ past: [], future: [] })
  const isUndoRedoRef = useRef(false)
  const lastSnapshotRef = useRef(null)

  // Load user's networks on mount
  useEffect(() => {
    if (user) {
      loadUserNetworks()
    }
  }, [user])

  // Smart auto-save - triggers on any change
  useEffect(() => {
    if (!user) return

    // If we have a network loaded or nodes exist, trigger auto-save
    if (nodes.length > 0 || edges.length > 0 || demands.length > 0) {
      setSaveStatus('unsaved')

      // Clear existing timeout
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout)
      }

      // Set new timeout for 3 seconds
      const timeout = setTimeout(() => {
        performAutoSave()
      }, 3000)

      setAutoSaveTimeout(timeout)

      return () => clearTimeout(timeout)
    }
  }, [user, nodes, edges, demands])

  // Undo/Redo - capture state snapshots on meaningful changes (debounced)
  useEffect(() => {
    if (isUndoRedoRef.current) return

    const snapshot = JSON.stringify({ nodes, edges, demands })
    // Only push if state actually changed
    if (snapshot !== lastSnapshotRef.current) {
      if (lastSnapshotRef.current !== null) {
        historyRef.current.past.push(lastSnapshotRef.current)
        // Cap history at 50 entries
        if (historyRef.current.past.length > 50) {
          historyRef.current.past.shift()
        }
        historyRef.current.future = []
      }
      lastSnapshotRef.current = snapshot
    }
  }, [nodes, edges, demands])

  const undo = useCallback(() => {
    const { past, future } = historyRef.current
    if (past.length === 0) return

    const previous = past.pop()
    future.push(lastSnapshotRef.current)

    isUndoRedoRef.current = true
    const state = JSON.parse(previous)
    setNodes(state.nodes)
    setEdges(state.edges)
    setDemands(state.demands)
    lastSnapshotRef.current = previous
    setSelectedNode(null)
    setSelectedEdge(null)

    // Reset flag after React processes the state updates
    setTimeout(() => { isUndoRedoRef.current = false }, 0)

  }, [setNodes, setEdges])

  const redo = useCallback(() => {
    const { past, future } = historyRef.current
    if (future.length === 0) return

    const next = future.pop()
    past.push(lastSnapshotRef.current)

    isUndoRedoRef.current = true
    const state = JSON.parse(next)
    setNodes(state.nodes)
    setEdges(state.edges)
    setDemands(state.demands)
    lastSnapshotRef.current = next
    setSelectedNode(null)
    setSelectedEdge(null)

    setTimeout(() => { isUndoRedoRef.current = false }, 0)

  }, [setNodes, setEdges])

  // Keep refs to latest values for keyboard handler
  const selectedNodeRef = useRef(selectedNode)
  selectedNodeRef.current = selectedNode
  const selectedEdgeRef = useRef(selectedEdge)
  selectedEdgeRef.current = selectedEdge
  const selectedDemandIdRef = useRef(selectedDemandId)
  selectedDemandIdRef.current = selectedDemandId
  const deleteNodeRef = useRef(null)
  const deleteEdgeRef = useRef(null)
  const deleteDemandRef = useRef(null)

  // Keyboard shortcuts: Delete, Ctrl+Z (undo), Ctrl+Y / Ctrl+Shift+Z (redo)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input, textarea, or contentEditable
      const tag = e.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return

      // Delete selected demand (customer)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedDemandIdRef.current) {
        e.preventDefault()
        if (deleteDemandRef.current) deleteDemandRef.current(selectedDemandIdRef.current)
        return
      }

      // Delete selected edge
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedEdgeRef.current && !selectedNodeRef.current) {
        e.preventDefault()
        if (deleteEdgeRef.current) deleteEdgeRef.current(selectedEdgeRef.current.id)
        return
      }

      // Delete selected node
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNodeRef.current) {
        e.preventDefault()
        if (deleteNodeRef.current) deleteNodeRef.current(selectedNodeRef.current.id)
        return
      }

      // Undo: Ctrl+Z (or Cmd+Z on Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
        return
      }

      // Redo: Ctrl+Y or Ctrl+Shift+Z (or Cmd+Shift+Z on Mac)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey) || (e.key === 'Z' && e.shiftKey))) {
        e.preventDefault()
        redo()
        return
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo])

  // Update edge colors when theme changes
  useEffect(() => {
    setEdges(eds =>
      eds.map(edge => ({
        ...edge,
        style: {
          ...edge.style,
          stroke: themeMode === 'light' ? '#4338ca' : '#818cf8',
        },
        labelStyle: {
          fill: themeMode === 'light' ? '#1a1d29' : '#f3f4f6',
          fontWeight: 600,
          fontSize: 12,
        },
        labelBgStyle: {
          fill: themeMode === 'light' ? '#ffffff' : '#1a1d29',
          fillOpacity: 0.9,
        },
      }))
    )
  }, [themeMode, setEdges])

  // Database Functions
  const loadUserNetworks = async () => {
    try {
      const { data, error } = await supabase
        .from('networks')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })

      if (error) throw error
      setSavedNetworks(data || [])
    } catch (error) {
      console.error('Error loading networks:', error)
    }
  }

  const performAutoSave = async () => {
    if (!user) return

    try {
      setSaveStatus('saving')

      const networkData = {
        name: currentNetworkName,
        nodes: nodes,
        edges: edges,
        demands: demands,
      }

      if (currentNetworkId) {
        // Update existing network
        const { error } = await supabase
          .from('networks')
          .update(networkData)
          .eq('id', currentNetworkId)

        if (error) throw error
      } else {
        // Create new network (first save)
        const { data, error } = await supabase
          .from('networks')
          .insert({
            user_id: user.id,
            ...networkData,
          })
          .select()
          .single()

        if (error) throw error
        setCurrentNetworkId(data.id)
        setCurrentNetworkName(data.name)
      }

      setSaveStatus('saved')

      // Reload networks list in background
      loadUserNetworks()
    } catch (error) {
      console.error('Auto-save error:', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('unsaved'), 3000)
    }
  }

  const saveNetworkAsNew = async () => {
    const name = prompt('Enter network name:', 'Copy of ' + currentNetworkName)
    if (!name) return

    try {
      setIsLoading(true)
      setLoadingMessage('Saving as new network...')

      const { data, error } = await supabase
        .from('networks')
        .insert({
          user_id: user.id,
          name,
          description: '',
          nodes: nodes,
          edges: edges,
          demands: demands,
        })
        .select()
        .single()

      if (error) throw error

      setCurrentNetworkId(data.id)
      setCurrentNetworkName(data.name)
      await loadUserNetworks()

      setSnackbar({
        open: true,
        message: `Saved as "${name}"`,
        severity: 'success',
      })
    } catch (error) {
      console.error('Error saving network:', error)
      setSnackbar({
        open: true,
        message: 'Failed to save network',
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
      setLoadingMessage('')
    }
  }

  const loadNetworkFromDatabase = async networkId => {
    try {
      setIsLoading(true)
      setLoadingMessage('Loading network...')

      const { data, error } = await supabase
        .from('networks')
        .select('*')
        .eq('id', networkId)
        .single()

      if (error) throw error

      // Migrate old nodes to use custom node type
      const migratedNodes = (data.nodes || []).map(node => ({
        ...node,
        type: node.data?.nodeType ? 'supplyChainNode' : node.type,
      }))
      setNodes(migratedNodes)
      setEdges(data.edges || [])
      setDemands(data.demands || [])
      setCurrentNetworkId(data.id)
      setCurrentNetworkName(data.name)
      setSaveStatus('saved')
      setShowSavedNetworks(false)

      setSnackbar({
        open: true,
        message: `Loaded: ${data.name}`,
        severity: 'success',
      })
    } catch (error) {
      console.error('Error loading network:', error)
      setSnackbar({
        open: true,
        message: 'Failed to load network',
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
      setLoadingMessage('')
    }
  }

  const deleteNetworkFromDatabase = async networkId => {
    try {
      const { error } = await supabase.from('networks').delete().eq('id', networkId)

      if (error) throw error

      if (currentNetworkId === networkId) {
        setCurrentNetworkId(null)
        setNodes([])
        setEdges([])
        setDemands([])
      }

      await loadUserNetworks()

      setSnackbar({
        open: true,
        message: 'Network deleted',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error deleting network:', error)
      setSnackbar({
        open: true,
        message: 'Failed to delete network',
        severity: 'error',
      })
    }
  }

  const renameNetwork = async (networkId, newName) => {
    try {
      const { error } = await supabase
        .from('networks')
        .update({ name: newName })
        .eq('id', networkId)

      if (error) throw error

      await loadUserNetworks()

      setSnackbar({
        open: true,
        message: 'Network renamed',
        severity: 'success',
      })
    } catch (error) {
      console.error('Error renaming network:', error)
      setSnackbar({
        open: true,
        message: 'Failed to rename network',
        severity: 'error',
      })
    }
  }

  // Helper: compute a neat workspace position based on node type
  const getWorkspacePosition = useCallback(
    type => {
      const typeColumns = { supplier: 0, factory: 1, distributor: 2, retailer: 3 }
      const col = typeColumns[type] ?? 0
      const existingOfType = nodes.filter(n => n.data.nodeType === type).length
      const x = 100 + col * 200
      const y = 80 + existingOfType * 120
      return { x, y }
    },
    [nodes]
  )

  // Helper: compute a default India map location based on node type
  const getDefaultMapLocation = useCallback(
    type => {
      const baseLocations = {
        supplier: { lat: 28.6, lng: 77.2 },    // Delhi area
        factory: { lat: 22.7, lng: 75.8 },      // Central India
        distributor: { lat: 19.1, lng: 72.9 },   // Mumbai area
        retailer: { lat: 13.1, lng: 80.3 },      // Chennai area
      }
      const base = baseLocations[type] || baseLocations.supplier
      const existingOfType = nodes.filter(n => n.data.nodeType === type).length
      // Offset each subsequent node slightly
      return {
        lat: base.lat + existingOfType * 0.8,
        lng: base.lng + existingOfType * 1.2,
      }
    },
    [nodes]
  )

  // Map node handlers
  const addNodeOnMap = useCallback(
    (type, lat, lng) => {
      const nodeCount = nodes.filter(n => n.data.nodeType === type).length + 1
      const typeLabel = type.charAt(0).toUpperCase() + type.slice(1)
      const wsPos = getWorkspacePosition(type)

      const baseNode = {
        id: `${type}_${Date.now()}`,
        type: 'supplyChainNode',
        data: {
          label: `${typeLabel} ${nodeCount}`,
          nodeType: type,
          lat,
          lng,
        },
        position: wsPos,
      }

      if (type === 'supplier') {
        baseNode.data = {
          ...baseNode.data,
          supplier_type: 'infinite',
          capacity: 10000,
          initial_level: 10000,
          holding_cost: 0.01,
          raw_material: null,
        }
        baseNode.style = {
          background: themeMode === 'light' ? '#10b981' : '#059669',
          border: themeMode === 'light' ? '2px solid #059669' : '2px solid #10b981',
          borderRadius: '14px',
          padding: 0,
          width: 52,
          height: 52,
          boxShadow:
            themeMode === 'light' ? '0 2px 8px rgba(16, 185, 129, 0.25)' : '0 2px 8px rgba(52, 211, 153, 0.2)',
        }
      } else if (type === 'factory') {
        baseNode.data = {
          ...baseNode.data,
          capacity: 2500,
          initial_level: 2500,
          holding_cost: 0.02,
          replenishment_policy: 'SS',
          policy_s: 1000,
          policy_S: 2500,
          policy_R: 7,
          policy_Q: 1500,
          sell_price: 30,
          product: null,
          manufacturing_cost: 20,
          manufacturing_time: 1,
          batch_size: 1000,
        }
        baseNode.style = {
          background: themeMode === 'light' ? '#f59e0b' : '#d97706',
          border: themeMode === 'light' ? '2px solid #d97706' : '2px solid #f59e0b',
          borderRadius: '14px',
          padding: 0,
          width: 52,
          height: 52,
          boxShadow:
            themeMode === 'light' ? '0 2px 8px rgba(245, 158, 11, 0.25)' : '0 2px 8px rgba(251, 191, 36, 0.2)',
        }
      } else if (type === 'distributor') {
        baseNode.data = {
          ...baseNode.data,
          capacity: 1000,
          initial_level: 1000,
          holding_cost: 0.22,
          replenishment_policy: 'SS',
          policy_s: 400,
          policy_S: 1000,
          policy_R: 7,
          policy_Q: 500,
          buy_price: 150,
          sell_price: 300,
        }
        baseNode.style = {
          background: themeMode === 'light' ? '#3b82f6' : '#2563eb',
          border: themeMode === 'light' ? '2px solid #2563eb' : '2px solid #3b82f6',
          borderRadius: '14px',
          padding: 0,
          width: 52,
          height: 52,
          boxShadow:
            themeMode === 'light' ? '0 2px 8px rgba(59, 130, 246, 0.25)' : '0 2px 8px rgba(96, 165, 250, 0.2)',
        }
      } else if (type === 'retailer') {
        baseNode.data = {
          ...baseNode.data,
          capacity: 500,
          initial_level: 500,
          holding_cost: 0.25,
          replenishment_policy: 'SS',
          policy_s: 200,
          policy_S: 500,
          policy_R: 5,
          policy_Q: 300,
          buy_price: 300,
          sell_price: 400,
        }
        baseNode.style = {
          background: themeMode === 'light' ? '#8b5cf6' : '#7c3aed',
          border: themeMode === 'light' ? '2px solid #7c3aed' : '2px solid #8b5cf6',
          borderRadius: '14px',
          padding: 0,
          width: 52,
          height: 52,
          boxShadow:
            themeMode === 'light' ? '0 2px 8px rgba(139, 92, 246, 0.25)' : '0 2px 8px rgba(167, 139, 250, 0.2)',
        }
      }

      setNodes([...nodes, baseNode])
      setMapPlacementMode(null)
      setSaveStatus('unsaved')

      setSnackbar({
        open: true,
        message: `${typeLabel} placed on map`,
        severity: 'success',
      })
    },
    [nodes, setNodes, themeMode]
  )

  const handleMapNodeMove = useCallback(
    (nodeId, lat, lng) => {
      setNodes(nds =>
        nds.map(node =>
          node.id === nodeId ? { ...node, data: { ...node.data, lat, lng } } : node
        )
      )
      setSaveStatus('unsaved')
    },
    [setNodes]
  )

  // Node Functions
  const addNode = type => {
    // If in map mode, enter placement mode instead of instant creation
    if (viewMode === 'map') {
      setMapPlacementMode(type)
      const typeLabel = type.charAt(0).toUpperCase() + type.slice(1)
      setSnackbar({
        open: true,
        message: `Click on the map to place ${typeLabel}`,
        severity: 'info',
      })
      return
    }

    const nodeCount = nodes.filter(n => n.data.nodeType === type).length + 1
    const typeLabel = type.charAt(0).toUpperCase() + type.slice(1)
    const defaultLoc = getDefaultMapLocation(type)
    const wsPos = getWorkspacePosition(type)

    // Base node configuration — includes default map location
    const baseNode = {
      id: `${type}_${Date.now()}`,
      type: 'supplyChainNode',
      data: {
        label: `${typeLabel} ${nodeCount}`,
        nodeType: type,
        lat: defaultLoc.lat,
        lng: defaultLoc.lng,
      },
      position: wsPos,
    }

    // Add type-specific defaults
    if (type === 'supplier') {
      baseNode.data = {
        ...baseNode.data,
        supplier_type: 'infinite', // 'infinite' or 'finite'
        capacity: 10000,
        initial_level: 10000,
        holding_cost: 0.01,
        // Raw material will be added via config panel
        raw_material: null,
      }
      baseNode.style = {
        background: themeMode === 'light' ? '#10b981' : '#059669',
        border: themeMode === 'light' ? '2px solid #059669' : '2px solid #10b981',
        borderRadius: '14px',
        padding: 0,
        width: 52,
        height: 52,
        boxShadow:
          themeMode === 'light' ? '0 2px 8px rgba(16, 185, 129, 0.25)' : '0 2px 8px rgba(52, 211, 153, 0.2)',
      }
    } else if (type === 'factory') {
      baseNode.data = {
        ...baseNode.data,
        capacity: 2500,
        initial_level: 2500,
        holding_cost: 0.02,
        replenishment_policy: 'SS',
        policy_s: 1000,
        policy_S: 2500,
        policy_R: 7,
        policy_Q: 1500,
        sell_price: 30,
        // Product will be created via config panel
        product: null,
        manufacturing_cost: 20,
        manufacturing_time: 1,
        batch_size: 1000,
      }
      baseNode.style = {
        background: themeMode === 'light' ? '#f59e0b' : '#d97706',
        border: themeMode === 'light' ? '2px solid #d97706' : '2px solid #f59e0b',
        borderRadius: '14px',
        padding: 0,
        width: 52,
        height: 52,
        boxShadow:
          themeMode === 'light' ? '0 2px 8px rgba(245, 158, 11, 0.25)' : '0 2px 8px rgba(251, 191, 36, 0.2)',
      }
    } else if (type === 'distributor') {
      baseNode.data = {
        ...baseNode.data,
        capacity: 1000,
        initial_level: 1000,
        holding_cost: 0.22,
        replenishment_policy: 'SS',
        policy_s: 400,
        policy_S: 1000,
        policy_R: 7,
        policy_Q: 500,
        buy_price: 150,
        sell_price: 300,
      }
      baseNode.style = {
        background: themeMode === 'light' ? '#3b82f6' : '#2563eb',
        border: themeMode === 'light' ? '2px solid #2563eb' : '2px solid #3b82f6',
        borderRadius: '14px',
        padding: 0,
        width: 52,
        height: 52,
        boxShadow:
          themeMode === 'light' ? '0 2px 8px rgba(59, 130, 246, 0.25)' : '0 2px 8px rgba(96, 165, 250, 0.2)',
      }
    } else if (type === 'retailer') {
      baseNode.data = {
        ...baseNode.data,
        capacity: 500,
        initial_level: 500,
        holding_cost: 0.25,
        replenishment_policy: 'SS',
        policy_s: 200,
        policy_S: 500,
        policy_R: 5,
        policy_Q: 300,
        buy_price: 300,
        sell_price: 400,
      }
      baseNode.style = {
        background: themeMode === 'light' ? '#8b5cf6' : '#7c3aed',
        border: themeMode === 'light' ? '2px solid #7c3aed' : '2px solid #8b5cf6',
        borderRadius: '14px',
        padding: 0,
        width: 52,
        height: 52,
        boxShadow:
          themeMode === 'light' ? '0 2px 8px rgba(139, 92, 246, 0.25)' : '0 2px 8px rgba(167, 139, 250, 0.2)',
      }
    }

    setNodes([...nodes, baseNode])
    setSaveStatus('unsaved')

    setSnackbar({
      open: true,
      message: `${typeLabel} added — drag it on the Map to set its exact location`,
      severity: 'success',
    })
  }

  const updateNodeData = (nodeId, newData) => {
    setNodes(nds =>
      nds.map(node => (node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node))
    )
  }

  const deleteNode = nodeId => {
    const node = nodes.find(n => n.id === nodeId)
    const connectedEdges = edges.filter(e => e.source === nodeId || e.target === nodeId)
    const affectedDemands = demands.filter(d => d.target_node === nodeId)

    let message = `Are you sure you want to delete "${node?.data.label}"?`
    if (connectedEdges.length > 0) {
      message += ` This will also remove ${connectedEdges.length} connected link(s).`
    }
    if (affectedDemands.length > 0) {
      message += ` ${affectedDemands.length} demand(s) will be reassigned to another node.`
    }

    setConfirmDialog({
      open: true,
      title: 'Delete Node?',
      message,
      severity: 'error',
      onConfirm: () => {
        // Remove node
        setNodes(nds => nds.filter(n => n.id !== nodeId))

        // Remove connected edges
        setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId))

        // Auto-fix demands - reassign to first available distributor/retailer
        const remainingNodes = nodes.filter(n => n.id !== nodeId)
        const fallbackNode =
          remainingNodes.find(
            n => n.data.nodeType === 'distributor' || n.data.nodeType === 'retailer'
          ) || remainingNodes[0]

        if (affectedDemands.length > 0 && fallbackNode) {
          setDemands(dems =>
            dems.map(d => (d.target_node === nodeId ? { ...d, target_node: fallbackNode.id } : d))
          )
        } else if (affectedDemands.length > 0 && !fallbackNode) {
          // No nodes left - delete demands
          setDemands(dems => dems.filter(d => d.target_node !== nodeId))
        }

        if (selectedNode?.id === nodeId) setSelectedNode(null)

        setSnackbar({
          open: true,
          message: 'Node deleted successfully',
          severity: 'success',
        })
      },
    })
  }
  deleteNodeRef.current = deleteNode

  const deleteEdge = (edgeId) => {
    setEdges(eds => eds.filter(e => e.id !== edgeId))
    setSelectedEdge(null)
    setSaveStatus('unsaved')
    setSnackbar({ open: true, message: 'Connection deleted', severity: 'success' })
  }
  deleteEdgeRef.current = deleteEdge

  // Edge Functions
  const onConnect = useCallback(
    params => {
      const sourceNode = nodes.find(n => n.id === params.source)
      const targetNode = nodes.find(n => n.id === params.target)

      if (!sourceNode || !targetNode) return

      if (sourceNode.data.nodeType === 'supplier' && targetNode.data.nodeType === 'supplier') {
        setSnackbar({
          open: true,
          message: 'Cannot connect supplier to supplier!',
          severity: 'error',
        })
        return
      }

      if (targetNode.data.nodeType === 'supplier') {
        setSnackbar({
          open: true,
          message: 'Suppliers cannot be target nodes!',
          severity: 'error',
        })
        return
      }

      const newEdge = {
        ...params,
        animated: true,
        type: 'smoothstep',
        data: { cost: 10, lead_time: 5 },
        label: `C: ${currency.symbol}10, LT: 5d`,
        style: {
          stroke: themeMode === 'light' ? '#4338ca' : '#818cf8',
          strokeWidth: 2.5,
        },
        labelStyle: {
          fill: themeMode === 'light' ? '#1a1d29' : '#f3f4f6',
          fontWeight: 600,
          fontSize: 12,
        },
        labelBgStyle: {
          fill: themeMode === 'light' ? '#ffffff' : '#1a1d29',
          fillOpacity: 0.9,
        },
      }

      setEdges(eds => addEdge(newEdge, eds))
      setSnackbar({
        open: true,
        message: 'Connection created!',
        severity: 'success',
      })
    },
    [setEdges, nodes, themeMode]
  )

  const updateEdgeData = (edgeId, newData) => {
    const sym = currency.symbol
    setEdges(eds =>
      eds.map(edge => {
        if (edge.id === edgeId) {
          const updatedData = { ...edge.data, ...newData }
          return {
            ...edge,
            data: updatedData,
            label: `C: ${sym}${updatedData.cost}, LT: ${updatedData.lead_time}d`,
          }
        }
        return edge
      })
    )
  }

  // Demand Functions
  // Demand Functions
  const addDemand = () => {
    const demandCount = demands.length + 1
    const targetNode =
      nodes.find(n => n.data.nodeType === 'distributor' || n.data.nodeType === 'retailer') ||
      nodes[0]

    if (!targetNode) {
      setSnackbar({
        open: true,
        message: 'Please add at least one node first!',
        severity: 'warning',
      })
      return
    }

    const newDemand = {
      id: `demand_${Date.now()}`,
      name: `Demand ${demandCount}`,
      target_node: targetNode.id,
      pattern: 'constant',
      patternParams: { interval: 1 },
      arrival_interval: 1,
      distribution_type: 'fixed',
      distribution_params: {},
      order_quantity: 400,
      delivery_cost: 10,
      lead_time: 5,
    }

    setDemands([...demands, newDemand])
    setSnackbar({
      open: true,
      message: `Demand ${demandCount} added!`,
      severity: 'success',
    })
  }

  const updateDemand = (demandId, newData) => {
    setDemands(dems => {
      const updated = dems.map(demand => {
        if (demand.id === demandId) {
          const result = {
            ...demand,
            ...newData,
          }
          return result
        }
        return demand
      })
      return updated
    })
  }

  const deleteDemand = demandId => {
    const demand = demands.find(d => d.id === demandId)
    const customerName = demand?.name?.replace(/^demand\s*/i, 'Customer ') || 'this customer'
    setConfirmDialog({
      open: true,
      title: 'Delete Customer?',
      message: `Are you sure you want to delete "${customerName}"?`,
      severity: 'error',
      onConfirm: () => {
        setDemands(dems => dems.filter(d => d.id !== demandId))
        setDemandNodePositions(prev => {
          const next = { ...prev }
          delete next[demandId]
          return next
        })
        setSelectedDemandId(null)
        setSnackbar({ open: true, message: 'Customer deleted', severity: 'success' })
      },
    })
  }
  deleteDemandRef.current = deleteDemand

  // Simulation
  const runSimulation = async () => {
    setIsSimulating(true)

    try {
      // Step 1: Auto-validate network
      console.log('Validating network before simulation...')
      const validation = validateNetwork(nodes, edges, demands)

      // Step 2: Check if validation passed (no errors)
      if (!validation.isValid) {
        // Show validation dialog with errors
        setValidationResults(validation)
        setShowValidation(true)

        const errorCount = validation.errors.length
        setSnackbar({
          open: true,
          message: `Cannot run simulation: ${errorCount} error(s) must be fixed first`,
          severity: 'error',
        })

        setIsSimulating(false)
        return // STOP - don't run simulation
      }

      // Step 3: Show warnings if any (but continue)
      if (validation.hasWarnings) {
        console.warn('Network has warnings:', validation.warnings)

        // Optionally show warnings but still proceed
        setSnackbar({
          open: true,
          message: `Running simulation with ${validation.warnings.length} warning(s)`,
          severity: 'warning',
        })

        // Small delay to let user see the warning
        await new Promise(resolve => setTimeout(resolve, 1500))
      }

      // Step 4: Validation passed - run simulation
      console.log('Validation passed, starting simulation...')

      // Start animation on map while waiting for results
      setShowSimAnimation(true)
      const animStartTime = Date.now()

      const response = await axios.post(`${API_BASE_URL}/simulate`, {
        nodes: nodes,
        links: edges,
        demands: demands,
        sim_time: simTime,
      })

      console.log('Simulation response:', response.data)

      if (response.data.success) {
        // Ensure animation plays for at least 4 seconds
        const elapsed = Date.now() - animStartTime
        const minAnimDuration = 4000
        if (elapsed < minAnimDuration) {
          await new Promise(resolve => setTimeout(resolve, minAnimDuration - elapsed))
        }

        setShowSimAnimation(false)
        setSimulationResults(response.data)
        setSnackbar({
          open: true,
          message: 'Simulation completed successfully!',
          severity: 'success',
        })
      } else {
        setShowSimAnimation(false)
        throw new Error(response.data.error || 'Simulation failed')
      }
    } catch (error) {
      console.error('Simulation error:', error)
      setShowSimAnimation(false)
      setSnackbar({
        open: true,
        message: `Simulation failed: ${error.response?.data?.detail || error.message}`,
        severity: 'error',
      })
    } finally {
      setIsSimulating(false)
    }
  }

  const runValidation = () => {
    const results = validateNetwork(nodes, edges, demands)
    setValidationResults(results)
    setShowValidation(true)
  }

  // File operations
  const saveConfiguration = () => {
    const config = { nodes, edges, demands }
    const blob = new Blob([JSON.stringify(config, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `supplynet-config-${Date.now()}.json`
    a.click()
    setSnackbar({
      open: true,
      message: 'Configuration exported!',
      severity: 'success',
    })
  }

  const loadConfiguration = event => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = e => {
      try {
        const config = JSON.parse(e.target.result)
        setNodes(config.nodes || [])
        setEdges(config.edges || [])
        setDemands(config.demands || [])
        setSnackbar({
          open: true,
          message: 'Configuration imported!',
          severity: 'success',
        })
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to import configuration',
          severity: 'error',
        })
      }
    }
    reader.readAsText(file)
    event.target.value = ''
  }

  const clearAll = () => {
    setConfirmDialog({
      open: true,
      title: 'Create New Network?',
      message: 'This will clear the current workspace. Your current network is auto-saved.',
      severity: 'warning',
      onConfirm: () => {
        setNodes([])
        setEdges([])
        setDemands([])
        setSelectedNode(null)
        setSelectedEdge(null)
        setSimulationResults(null)
        setCurrentNetworkId(null)
        setCurrentNetworkName('Untitled Network')
        setSaveStatus('unsaved')
        setSnackbar({
          open: true,
          message: 'New workspace created',
          severity: 'info',
        })
      },
    })
  }

  const createNewNetwork = () => {
    setConfirmDialog({
      open: true,
      title: 'Create New Network?',
      message: 'Your current work will be saved. Start a new network?',
      severity: 'info',
      onConfirm: () => {
        setNodes([])
        setEdges([])
        setDemands([])
        setSelectedNode(null)
        setSelectedEdge(null)
        setSimulationResults(null)
        setCurrentNetworkId(null)
        setCurrentNetworkName('Untitled Network')
        setSaveStatus('unsaved')
      },
    })
  }

  const renameCurrentNetwork = async (providedName) => {
    const newName = providedName || prompt('Enter network name:', currentNetworkName)
    if (!newName || newName === currentNetworkName) return

    setCurrentNetworkName(newName)

    if (currentNetworkId) {
      try {
        await supabase.from('networks').update({ name: newName }).eq('id', currentNetworkId)

        await loadUserNetworks()

        setSnackbar({
          open: true,
          message: 'Network renamed',
          severity: 'success',
        })
      } catch (error) {
        console.error('Error renaming:', error)
      }
    }
  }

  const handleLogout = async () => {
    setConfirmDialog({
      open: true,
      title: 'Logout?',
      message: 'Are you sure you want to logout? Unsaved changes will be lost.',
      severity: 'warning',
      onConfirm: async () => {
        await signOut()
        navigate('/')
      },
    })
  }

  // Event Handlers
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node)
    setSelectedEdge(null)
    setSelectedDemandId(null)
  }, [])

  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdge(edge)
    setSelectedNode(null)
    setSelectedDemandId(null)
  }, [])

  const handleDemandClick = useCallback((demandId) => {
    setSelectedDemandId(demandId)
    setSelectedNode(null)
    setSelectedEdge(null)
  }, [])

  // Config panel resize handlers
  const handleConfigResizeStart = useCallback(e => {
    e.preventDefault()
    isResizingConfig.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const handleMouseMove = ev => {
      if (!isResizingConfig.current) return
      const newWidth = window.innerWidth - ev.clientX
      setConfigPanelWidth(Math.max(260, Math.min(newWidth, window.innerWidth * 0.6)))
    }
    const handleMouseUp = () => {
      isResizingConfig.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [])

  const exportResults = () => {
    if (!simulationResults) return

    try {
      // ============ SHEET 1: NETWORK OVERVIEW ============
      const networkData = [
        ['=== SUPPLY CHAIN SIMULATION REPORT ==='],
        [`Generated: ${new Date().toLocaleString()}`],
        [''],
        ['NETWORK SUMMARY'],
        ['Metric', 'Value'],
        ['Simulation Duration', `${simTime} days`],
        ['Total Nodes', simulationResults.metrics.num_of_nodes],
        ['Total Links', simulationResults.metrics.num_of_links],
        ['Suppliers', simulationResults.metrics.num_suppliers],
        ['Distributors', simulationResults.metrics.num_distributors],
        ['Manufacturers', simulationResults.metrics.num_manufacturers],
        ['Retailers', simulationResults.metrics.num_retailers],
        [''],
        ['FINANCIAL METRICS'],
        ['Metric', 'Value'],
        ['Profit', `$${simulationResults.metrics.profit?.toLocaleString()}`],
        ['Revenue', `$${simulationResults.metrics.revenue?.toLocaleString()}`],
        ['Total Cost', `$${simulationResults.metrics.total_cost?.toFixed(2)}`],
        ['Inventory Carry Cost', `$${simulationResults.metrics.inventory_carry_cost?.toFixed(2)}`],
        ['Inventory Spend Cost', `$${simulationResults.metrics.inventory_spend_cost?.toFixed(2)}`],
        ['Transportation Cost', `$${simulationResults.metrics.transportation_cost?.toFixed(2)}`],
        ['Avg Cost per Order', `$${simulationResults.metrics.avg_cost_per_order?.toFixed(2)}`],
        ['Avg Cost per Item', `$${simulationResults.metrics.avg_cost_per_item?.toFixed(4)}`],
        [''],
        ['INVENTORY & DEMAND'],
        ['Metric', 'Value'],
        ['Available Inventory', `${simulationResults.metrics.available_inv} units`],
        [
          'Avg Available Inventory',
          `${simulationResults.metrics.avg_available_inv?.toFixed(0)} units`,
        ],
        ['Total Demand (Orders)', simulationResults.metrics.total_demand?.[0] || 0],
        ['Total Demand (Units)', simulationResults.metrics.total_demand?.[1] || 0],
        ['Customer Demand (Orders)', simulationResults.metrics.demand_by_customers?.[0] || 0],
        ['Customer Demand (Units)', simulationResults.metrics.demand_by_customers?.[1] || 0],
        ['Shortage (Orders)', simulationResults.metrics.shortage?.[0] || 0],
        ['Shortage (Units)', simulationResults.metrics.shortage?.[1] || 0],
        ['Backorders (Orders)', simulationResults.metrics.backorders?.[0] || 0],
        ['Backorders (Units)', simulationResults.metrics.backorders?.[1] || 0],
        [
          'Fulfillment Received (Units)',
          simulationResults.metrics.fulfillment_received_by_customers?.[1] || 0,
        ],
      ]

      // ============ SHEET 2: INDIVIDUAL NODES ============
      const individualNodesData = [['=== INDIVIDUAL NODE PERFORMANCE ==='], ['']]

      if (simulationResults.individual_stats) {
        // Group by type
        const groupedNodes = {}
        Object.entries(simulationResults.individual_stats).forEach(([nodeId, nodeData]) => {
          const type = nodeData.type
          if (!groupedNodes[type]) groupedNodes[type] = []
          groupedNodes[type].push({ nodeId, nodeData })
        })

        // Sort types
        const typeOrder = ['supplier', 'factory', 'manufacturer', 'distributor', 'retailer']
        const sortedTypes = Object.keys(groupedNodes).sort((a, b) => {
          const aIndex = typeOrder.findIndex(t => a.includes(t))
          const bIndex = typeOrder.findIndex(t => b.includes(t))
          return aIndex - bIndex
        })

        sortedTypes.forEach(type => {
          const nodesOfType = groupedNodes[type]

          nodesOfType.forEach(node => {
            const { nodeData } = node
            const stats = nodeData.stats

            individualNodesData.push([`${nodeData.name.toUpperCase()} (${nodeData.type})`])
            individualNodesData.push(['Metric', 'Value'])
            individualNodesData.push(['Profit', `$${stats.profit?.toLocaleString() || 0}`])
            individualNodesData.push(['Revenue', `$${stats.revenue?.toLocaleString() || 0}`])
            individualNodesData.push(['Node Cost', `$${stats.node_cost?.toFixed(2) || 0}`])
            individualNodesData.push(['Inventory Level', `${stats.inventory_level || 0} units`])
            individualNodesData.push(['Inventory Waste', `${stats.inventory_waste || 0} units`])
            individualNodesData.push([
              'Inventory Carry Cost',
              `$${stats.inventory_carry_cost?.toFixed(2) || 0}`,
            ])
            individualNodesData.push([
              'Inventory Spend Cost',
              `$${stats.inventory_spend_cost?.toFixed(2) || 0}`,
            ])
            individualNodesData.push([
              'Transportation Cost',
              `$${stats.transportation_cost?.toFixed(2) || 0}`,
            ])
            individualNodesData.push(['Demand Placed (Orders)', stats.demand_placed?.[0] || 0])
            individualNodesData.push(['Demand Placed (Units)', stats.demand_placed?.[1] || 0])
            individualNodesData.push([
              'Fulfillment Received (Orders)',
              stats.fulfillment_received?.[0] || 0,
            ])
            individualNodesData.push([
              'Fulfillment Received (Units)',
              stats.fulfillment_received?.[1] || 0,
            ])
            individualNodesData.push(['Demand Received (Orders)', stats.demand_received?.[0] || 0])
            individualNodesData.push(['Demand Received (Units)', stats.demand_received?.[1] || 0])
            individualNodesData.push([
              'Demand Fulfilled (Orders)',
              stats.demand_fulfilled?.[0] || 0,
            ])
            individualNodesData.push(['Demand Fulfilled (Units)', stats.demand_fulfilled?.[1] || 0])
            individualNodesData.push(['Orders Shortage (Orders)', stats.orders_shortage?.[0] || 0])
            individualNodesData.push(['Orders Shortage (Units)', stats.orders_shortage?.[1] || 0])
            individualNodesData.push(['Backorder (Orders)', stats.backorder?.[0] || 0])
            individualNodesData.push(['Backorder (Units)', stats.backorder?.[1] || 0])
            individualNodesData.push(['']) // Empty row between nodes
          })
        })
      }

      // ============ SHEET 3: NETWORK CONFIGURATION ============
      const configData = [
        ['=== NETWORK CONFIGURATION ==='],
        [''],
        ['NODES'],
        ['Node ID', 'Name', 'Type', 'Capacity', 'Initial Level', 'Holding Cost'],
      ]

      nodes.forEach(node => {
        configData.push([
          node.id,
          node.data.label,
          node.data.nodeType,
          node.data.capacity || 'N/A',
          node.data.initial_level || 'N/A',
          node.data.holding_cost || 'N/A',
        ])
      })

      configData.push([''])
      configData.push(['LINKS'])
      configData.push(['Link ID', 'Source', 'Target', 'Cost', 'Lead Time'])

      edges.forEach(edge => {
        const sourceName = nodes.find(n => n.id === edge.source)?.data.label || edge.source
        const targetName = nodes.find(n => n.id === edge.target)?.data.label || edge.target
        configData.push([edge.id, sourceName, targetName, edge.data.cost, edge.data.lead_time])
      })

      configData.push([''])
      configData.push(['DEMANDS'])
      configData.push(['Demand ID', 'Name', 'Target Node', 'Arrival Interval', 'Order Quantity'])

      demands.forEach(demand => {
        const targetName =
          nodes.find(n => n.id === demand.target_node)?.data.label || demand.target_node
        configData.push([
          demand.id,
          demand.name,
          targetName,
          demand.arrival_interval,
          demand.order_quantity,
        ])
      })

      // ============ COMBINE INTO SINGLE CSV WITH SHEET SEPARATORS ============
      const allData = [
        ...networkData,
        [''],
        [''],
        ['========================================'],
        [''],
        [''],
        ...individualNodesData,
        [''],
        [''],
        ['========================================'],
        [''],
        [''],
        ...configData,
      ]

      const csvContent = allData.map(row => row.join(',')).join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `supply-chain-complete-report-${Date.now()}.csv`
      a.click()

      setSnackbar({
        open: true,
        message: 'Complete report exported to CSV',
        severity: 'success',
      })
    } catch (error) {
      console.error('Export error:', error)
      setSnackbar({
        open: true,
        message: 'Failed to export results',
        severity: 'error',
      })
    }
  }

  const saveAsScenario = () => {
    if (!simulationResults) return

    const scenarioName = prompt('Enter scenario name:')
    if (!scenarioName) return

    const newScenario = {
      id: `scenario_${Date.now()}`,
      name: scenarioName,
      metrics: simulationResults.metrics,
      timestamp: new Date().toISOString(),
    }

    setScenarios([...scenarios, newScenario])
    setSnackbar({
      open: true,
      message: `Scenario "${scenarioName}" saved!`,
      severity: 'success',
    })
  }

  const toggleComparisonView = () => {
    if (scenarios.length < 2) {
      setSnackbar({
        open: true,
        message: 'Save at least 2 scenarios to compare',
        severity: 'warning',
      })
      return
    }
    setShowComparison(!showComparison)
  }

  // Filtered nodes for ReactFlow workspace based on visibility toggles
  const visibleWorkspaceNodes = useMemo(() => {
    return nodes.filter(n => visibility[n.data.nodeType] !== false)
  }, [nodes, visibility])

  const visibleWorkspaceEdges = useMemo(() => {
    if (!visibility.connections) return []
    const visibleIds = new Set(nodes.filter(n => visibility[n.data.nodeType] !== false).map(n => n.id))
    return edges.filter(e => visibleIds.has(e.source) && visibleIds.has(e.target))
  }, [edges, nodes, visibility])

  // Get fresh node data for ConfigPanel
  const freshSelectedNode = useMemo(() => {
    if (!selectedNode) return null
    return nodes.find(n => n.id === selectedNode.id) || null
  }, [selectedNode?.id, nodes])

  // Get fresh edge data for ConfigPanel (same pattern — selectedEdge is stale after edits)
  const freshSelectedEdge = useMemo(() => {
    if (!selectedEdge) return null
    return edges.find(e => e.id === selectedEdge.id) || null
  }, [selectedEdge?.id, edges])

  // Re-label all edges when currency changes
  useEffect(() => {
    const sym = currency.symbol
    setEdges(eds => eds.map(edge => ({
      ...edge,
      label: `C: ${sym}${edge.data?.cost ?? 10}, LT: ${edge.data?.lead_time ?? 5}d`,
    })))
  }, [currency.symbol, setEdges])

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isLoading && <LoadingOverlay message={loadingMessage} />}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            bgcolor: 'background.default',
            transition: 'background-color 0.3s ease',
          }}
        >
          <ActionToolbar
            onAddNode={addNode}
            onAddDemand={addDemand}
            onSaveConfig={saveConfiguration}
            onLoadConfig={() => document.querySelector('input[type="file"]').click()}
            onViewNetworks={() => setShowSavedNetworks(true)}
            onSaveScenario={saveAsScenario}
            onCompareScenarios={toggleComparisonView}
            onRunSimulation={runSimulation}
            onClearAll={clearAll}
            onValidate={runValidation}
            simTime={simTime}
            onSimTimeChange={setSimTime}
            onNewNetwork={createNewNetwork}
            onRenameNetwork={renameCurrentNetwork}
            onSaveAsNew={saveNetworkAsNew}
            isSimulating={isSimulating}
            hasResults={simulationResults !== null}
            stats={{
              nodes: nodes.length,
              links: edges.length,
              demands: demands.length,
              savedNetworks: savedNetworks.length,
              scenarios: scenarios.length,
            }}
            onMenuClick={action => {
              if (action === 'logout') handleLogout()
              else if (action === 'settings') console.log('Settings clicked')
              else if (action === 'help') console.log('Help clicked')
              else if (action === 'about') console.log('About clicked')
            }}
            themeMode={themeMode}
            onThemeToggle={toggleTheme}
            user={user}
          />

          {/* Hidden file input */}
          <input
            type="file"
            hidden
            accept=".json"
            onChange={loadConfiguration}
            style={{ display: 'none' }}
          />

          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <Box
              ref={canvasContainerRef}
              sx={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                background:
                  viewMode === 'map'
                    ? 'transparent'
                    : themeMode === 'light'
                      ? 'linear-gradient(160deg, #c2cede 0%, #bac9d8 60%, #c0cdd9 100%)'
                      : 'linear-gradient(160deg, #0b1220 0%, #0d1626 60%, #0a1320 100%)',
                transition: 'background 0.3s ease',
              }}
            >
              {/* Floating View Toggle - top center */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10,
                }}
              >
                <ViewToggle viewMode={viewMode} onChange={setViewMode} />
              </Box>

              {/* Floating Stats + Show visibility panel */}
              <WorkspaceStats
                stats={{
                  nodes: nodes.length,
                  links: edges.length,
                  demands: demands.length,
                }}
                onToggleFullscreen={handleToggleFullscreen}
                isFullscreen={isMapFullscreen}
                visibility={visibility}
                onToggleVisibility={handleToggleVisibility}
              />

              {viewMode === 'workspace' ? (
                <>
                  <WorkspaceVisibilityContext.Provider value={visibility}>
                  <ReactFlow
                    nodes={visibleWorkspaceNodes}
                    edges={visibleWorkspaceEdges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    onNodesChange={handleNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={onNodeClick}
                    onEdgeClick={onEdgeClick}
                    onPaneClick={() => setSelectedDemandId(null)}
                    proOptions={{ hideAttribution: true }}
                    fitView
                    deleteKeyCode={null}
                    nodesDraggable={!isWorkspaceLocked}
                    nodesConnectable={!isWorkspaceLocked}
                    elementsSelectable={!isWorkspaceLocked}
                    style={{ background: 'transparent' }}
                  >
                    <WorkspaceControls
                      themeMode={themeMode}
                      showMinimap={showMinimap}
                      onToggleMinimap={() => setShowMinimap(v => !v)}
                      isLocked={isWorkspaceLocked}
                      onToggleLock={() => setIsWorkspaceLocked(v => !v)}
                    />
                    {showMinimap && (
                      <MiniMap
                        nodeColor={node => {
                          const colors = { supplier: '#10b981', factory: '#f59e0b', distributor: '#3b82f6', retailer: '#8b5cf6' }
                          return colors[node.data.nodeType] || '#94a3b8'
                        }}
                        maskColor={themeMode === 'light' ? 'rgba(0,0,0,0.07)' : 'rgba(0,0,0,0.4)'}
                        style={{
                          width: 140,
                          height: 90,
                          backgroundColor: themeMode === 'light' ? '#f8fafc' : '#131f35',
                          border: `1px solid ${themeMode === 'light' ? '#d1d9e6' : 'rgba(255,255,255,0.08)'}`,
                          borderRadius: '8px',
                        }}
                      />
                    )}
                    <Background
                      variant="dots"
                      gap={20}
                      size={1.5}
                      color={themeMode === 'light' ? 'rgba(70,95,120,0.35)' : 'rgba(255,255,255,0.13)'}
                    />
                    <WorkspaceDemandLayer
                      demands={demands}
                      demandNodePositions={demandNodePositions}
                      onPositionChange={handleDemandPositionChange}
                      onDemandClick={handleDemandClick}
                      selectedDemandId={selectedDemandId}
                    />
                  </ReactFlow>
                  </WorkspaceVisibilityContext.Provider>
                </>
              ) : (
                <MapView
                  nodes={nodes}
                  edges={edges}
                  onNodeClick={onNodeClick}
                  onEdgeClick={onEdgeClick}
                  onNodePlace={addNodeOnMap}
                  onNodeMove={handleMapNodeMove}
                  onConnect={onConnect}
                  selectedNode={selectedNode}
                  themeMode={themeMode}
                  simulationResults={simulationResults}
                  showSimAnimation={showSimAnimation}
                  placementMode={mapPlacementMode}
                  onCancelPlacement={() => setMapPlacementMode(null)}
                  onViewNodeResults={(nodeId) => setDashboardNodeId(nodeId)}
                  visibility={visibility}
                  demands={demands}
                  onDemandClick={handleDemandClick}
                  selectedDemandId={selectedDemandId}
                />
              )}
            </Box>

            {/* Drag handle — config panel resizer */}
            <Box
              onMouseDown={handleConfigResizeStart}
              sx={{
                width: '5px',
                cursor: 'col-resize',
                flexShrink: 0,
                bgcolor: 'divider',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  bgcolor: 'primary.main',
                },
                '&:active': {
                  bgcolor: 'primary.main',
                },
              }}
            />

            <Box
              sx={{
                width: configPanelWidth,
                minWidth: 260,
                flexShrink: 0,
                overflow: 'auto',
                bgcolor: 'background.paper',
                boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
              }}
            >
              <ConfigPanel
                selectedNode={freshSelectedNode}
                updateNodeData={updateNodeData}
                selectedEdge={freshSelectedEdge}
                updateEdgeData={updateEdgeData}
                demands={demands}
                updateDemand={updateDemand}
                deleteDemand={deleteDemand}
                nodes={nodes}
                currentNetworkName={currentNetworkName}
                saveStatus={saveStatus}
                onRenameNetwork={renameCurrentNetwork}
              />
            </Box>
          </Box>

          {simulationResults && (
            <SimulationDashboard
              results={simulationResults}
              nodes={nodes}
              edges={edges}
              demands={demands}
              simTime={simTime}
              onClose={() => { setSimulationResults(null); setDashboardNodeId('network') }}
              onExport={exportResults}
              initialNodeId={dashboardNodeId}
            />
          )}

          {showComparison && (
            <ScenarioComparison
              scenarios={scenarios}
              onClose={() => setShowComparison(false)}
              onDelete={scenarioId => {
                setScenarios(scenarios.filter(s => s.id !== scenarioId))
                setSnackbar({
                  open: true,
                  message: 'Scenario deleted',
                  severity: 'success',
                })
              }}
            />
          )}

          {showValidation && validationResults && (
            <ValidationPanel
              validation={validationResults}
              onClose={() => setShowValidation(false)}
            />
          )}

          <SavedNetworksDialog
            open={showSavedNetworks}
            onClose={() => setShowSavedNetworks(false)}
            networks={savedNetworks}
            onLoad={loadNetworkFromDatabase}
            onDelete={deleteNetworkFromDatabase}
            onRename={renameNetwork}
          />

          <ConfirmDialog
            open={confirmDialog.open}
            onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
            onConfirm={confirmDialog.onConfirm}
            title={confirmDialog.title}
            message={confirmDialog.message}
            severity={confirmDialog.severity}
          />

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

function App() {
  const [showPreloader, setShowPreloader] = useState(true)
  const [preloaderFadeOut, setPreloaderFadeOut] = useState(false)

  const themeMode = localStorage.getItem('themeMode') || 'light'

  useEffect(() => {
    // Minimum preloader display time
    const timer = setTimeout(() => {
      setPreloaderFadeOut(true)
      setTimeout(() => setShowPreloader(false), 500) // Remove after fade
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* Main Content - ALWAYS rendered from start */}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/confirm" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          }
        />
        <Route path="/community" element={<CommunityHome />} />
        <Route
          path="/community/new"
          element={
            <PrivateRoute>
              <NewPost />
            </PrivateRoute>
          }
        />
        <Route path="/community/post/:postId" element={<PostDetail />} />
        <Route path="/community/announcements" element={<Announcements />} />
        <Route path="/community/guidelines" element={<Guidelines />} />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <MainApp />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Preloader Overlay - Covers content until ready */}
      {showPreloader && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            opacity: preloaderFadeOut ? 0 : 1,
            transition: 'opacity 0.5s ease-out',
            pointerEvents: 'none',
          }}
        >
          <Preloader darkMode={themeMode === 'dark'} />
        </Box>
      )}
    </Box>
  )
}

export default App
